import { z } from 'zod';
import { calendarDateSchema, canonicalUtcInstantSchema, competitionRefSchema, historicalTeamRefSchema, opaquePublicIdSchema, pageMetaSchema, publicCompetitionCodeSchema, publicEnvelopeSchema, scoreSchema, teamRefSchema, } from './football-public.js';
export const historyCoverageCaveatSchema = z.enum([
    'partial_coverage',
    'field_not_covered',
    'historical_only',
    'membership_unconfirmed',
    'source_limited',
]);
export const historyCoverageSchema = z.object({
    sampleSize: z.number().int().min(0),
    from: calendarDateSchema.nullable(),
    to: calendarDateSchema.nullable(),
    completeness: z.enum(['complete', 'partial', 'unknown']),
    caveats: z.array(historyCoverageCaveatSchema),
    source: z.enum(['api_football', 'ballpeek_projection', 'ballpeek_historical_archive']),
    projectedAt: canonicalUtcInstantSchema,
}).strip();
export const historicalMatchSummarySchema = z.object({
    detailKind: z.enum(['canonical', 'historical']),
    matchId: opaquePublicIdSchema.nullable(),
    historicalMatchId: opaquePublicIdSchema.nullable(),
    competition: competitionRefSchema,
    kickoffAt: canonicalUtcInstantSchema.nullable(),
    matchDate: calendarDateSchema,
    homeTeam: historicalTeamRefSchema,
    awayTeam: historicalTeamRefSchema,
    score: scoreSchema.nullable(),
}).strip().superRefine((match, context) => {
    const hasCanonicalId = match.matchId !== null;
    const hasHistoricalId = match.historicalMatchId !== null;
    if (hasCanonicalId === hasHistoricalId) {
        context.addIssue({
            code: 'custom',
            path: ['matchId'],
            message: 'Exactly one detail identifier is required',
        });
    }
    if ((match.detailKind === 'canonical') !== hasCanonicalId) {
        context.addIssue({
            code: 'custom',
            path: ['detailKind'],
            message: 'Detail kind must match the available identifier',
        });
    }
});
const pagingSchema = {
    page: z.coerce.number().int().min(1).max(1000).optional().default(1),
    pageSize: z.coerce.number().int().min(1).max(50).optional().default(20),
};
function validateHistoryScope(query, context) {
    if ((query.competitionCode === undefined) !== (query.season === undefined)) {
        context.addIssue({
            code: 'custom',
            path: query.competitionCode === undefined ? ['competitionCode'] : ['season'],
            message: 'competitionCode and season must be provided together',
        });
    }
    if (query.from !== undefined && query.to !== undefined && query.from > query.to) {
        context.addIssue({
            code: 'custom',
            path: ['to'],
            message: 'to must be on or after from',
        });
    }
}
export const teamHistoryParamsSchema = z.object({
    teamId: opaquePublicIdSchema,
}).strict();
export const teamHistoryQuerySchema = z.object({
    competitionCode: publicCompetitionCodeSchema.optional(),
    season: z.coerce.number().int().optional(),
    from: calendarDateSchema.optional(),
    to: calendarDateSchema.optional(),
    ...pagingSchema,
}).strict().superRefine(validateHistoryScope);
export const teamHistoryResponseSchema = publicEnvelopeSchema(z.object({
    team: teamRefSchema,
    filters: z.object({
        competitionCode: publicCompetitionCodeSchema.nullable(),
        season: z.number().int().nullable(),
        from: calendarDateSchema.nullable(),
        to: calendarDateSchema.nullable(),
    }).strip(),
    coverage: historyCoverageSchema,
    items: z.array(historicalMatchSummarySchema),
    pagination: pageMetaSchema,
}).strip());
export const historicalMatchDetailParamsSchema = z.object({
    historicalMatchId: opaquePublicIdSchema,
}).strict();
export const historicalStatisticSchema = z.object({
    metric: z.enum([
        'possession_pct',
        'shots',
        'shots_on_target',
        'corners',
        'fouls',
        'yellow_cards',
        'red_cards',
        'passes',
        'pass_accuracy_pct',
        'other_supported',
    ]),
    home: z.number().nullable(),
    away: z.number().nullable(),
    unit: z.enum(['count', 'percent']),
}).strip();
export const historicalMatchDetailResponseSchema = publicEnvelopeSchema(z.object({
    historicalMatchId: opaquePublicIdSchema,
    identityKind: z.literal('historical'),
    competition: competitionRefSchema,
    kickoffAt: canonicalUtcInstantSchema.nullable(),
    matchDate: calendarDateSchema,
    homeTeam: historicalTeamRefSchema,
    awayTeam: historicalTeamRefSchema,
    score: scoreSchema.nullable(),
    statistics: z.array(historicalStatisticSchema),
    coverage: historyCoverageSchema,
    links: z.object({
        homeTeamProfileAvailable: z.boolean(),
        awayTeamProfileAvailable: z.boolean(),
        headToHeadAvailable: z.boolean(),
    }).strip(),
}).strip());
export const headToHeadQuerySchema = z.object({
    teamAId: opaquePublicIdSchema,
    teamBId: opaquePublicIdSchema,
    competitionCode: publicCompetitionCodeSchema.optional(),
    season: z.coerce.number().int().optional(),
    from: calendarDateSchema.optional(),
    to: calendarDateSchema.optional(),
    ...pagingSchema,
}).strict().superRefine((query, context) => {
    validateHistoryScope(query, context);
    if (query.teamAId === query.teamBId) {
        context.addIssue({
            code: 'custom',
            path: ['teamBId'],
            message: 'teamBId must differ from teamAId',
        });
    }
});
export const headToHeadResponseSchema = publicEnvelopeSchema(z.object({
    teamA: teamRefSchema,
    teamB: teamRefSchema,
    filters: z.object({
        competitionCode: publicCompetitionCodeSchema.nullable(),
        season: z.number().int().nullable(),
        from: calendarDateSchema.nullable(),
        to: calendarDateSchema.nullable(),
    }).strip(),
    coverage: historyCoverageSchema,
    summary: z.object({
        meetings: z.number().int().min(0),
        teamAWins: z.number().int().min(0),
        draws: z.number().int().min(0),
        teamBWins: z.number().int().min(0),
        teamAGoals: z.number().int().min(0),
        teamBGoals: z.number().int().min(0),
    }).strip(),
    items: z.array(historicalMatchSummarySchema),
    pagination: pageMetaSchema,
}).strip());
